import { consume } from "@lit/context";
import {
    Absence,
    AbsenceStatus,
    type AbsenceType,
    assignEmployee,
    dateAdd,
    type DateRange,
    dateSub,
    formatDate,
    formatRange,
    GetBalancesParams,
    GetCommonWorkDaysParams,
    getHolidayForDate,
    getRange,
    GetRosterNotesParams,
    getVacationEntitlement,
    GLOBAL_CONSTANTS,
    isHalfDayEntry,
    parseDateString,
    type RosterNoteDTO,
    TimeEntry,
    TimeEntryType,
    timeEntryTypeColor,
    toDateString,
    type VacationBalance,
    VacationPolicy,
} from "@pentacode/core";
import type { DateString, Days } from "@pentacode/openapi";
import { css, html, nothing } from "lit";
import { customElement, query, queryAll, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { styleMap } from "lit/directives/style-map.js";
import "../components/avatar/avatar";
import "../components/roster-notes/roster-note";
import { Textarea } from "../components/text-area/textarea";
import { enabledFeatures, type EnabledFeatures } from "../contexts/features-context";
import { app } from "../init";
import { alert, confirm } from "./alert-dialog";
import { DateInput } from "./date-input";
import { Dialog } from "./dialog";
import { prompt } from "./prompt";
import "./scroller";

type Result = { updated?: boolean; created?: boolean; deleted?: boolean; shiftsUnassigned?: boolean };

@customElement("ptc-absence-dialog")
export class AbsenceDialog extends Dialog<Absence, Result> {
    readonly preventDismiss = true;

    @consume({ context: enabledFeatures, subscribe: true })
    @state()
    private _enabledFeatures!: EnabledFeatures;

    @query("form")
    private _form: HTMLFormElement;

    @query("[name='start']")
    private _startInput: DateInput;

    @query("[name='end']")
    private _endInput: DateInput;

    @query("[name='notes']")
    private _notesInput: Textarea;

    @state()
    private _absence: Absence;

    @state()
    private _entries: TimeEntry[] = [];

    @state()
    private _commonWorkDays: number[] = [];

    @state()
    private _vacationBalances: VacationBalance[] = [];

    @state()
    private _rosterNotes: RosterNoteDTO[] = [];

    @queryAll("input[name='days']")
    private _dayInputs: HTMLInputElement[];

    @state()
    private _validationError = "";

    @state()
    private _absenceDays = new Map<DateString, { type: TimeEntryType; isHalfDay: boolean }>();

    private get _employee() {
        return app.getEmployee(this._absence.employeeId)!;
    }

    private get _data() {
        if (!this._form) {
            return;
        }

        const data = new FormData(this._form);
        return {
            start: data.get("start") as DateString,
            end: data.get("end") ? dateAdd(data.get("end") as DateString, { days: 1 }) : null,
            notes: data.get("notes") as string,
        };
    }

    private get _days() {
        if (!this._data) {
            return;
        }
        const { start, end } = this._data;

        if (!start || !end) {
            return;
        }

        const { from } = getRange(start, "week");
        const { to } = getRange(dateAdd(end, { days: -1 }), "week");

        const days: DateString[] = [];
        let date = from;

        while (date < to) {
            days.push(date);
            date = dateAdd(date, { days: 1 });
        }

        return days;
    }

    private get _includedTypes() {
        const absenceType = this._absence.type;
        return absenceType === TimeEntryType.Vacation ? [TimeEntryType.Vacation, TimeEntryType.CompDay] : [absenceType];
    }

    private get _includedEntries() {
        const includedTypes = this._includedTypes;
        return this._entries.filter(
            (e) => e.date >= this._absence.start && e.date < this._absence.end && includedTypes.includes(e.type)
        );
    }

    private get _removedEntries() {
        return this._includedEntries.filter((e) => {
            const day = this._absenceDays.get(e.date);
            return !day || day.type !== e.type;
        });
    }

    private get _updatedEntries() {
        const data = this._data;

        if (!data) {
            return [];
        }

        const existing = this._includedEntries.filter((e) => {
            const day = this._absenceDays.get(e.date);
            return day && day.type === e.type;
        });

        const added = [...this._absenceDays.entries()]
            .filter(([date, { type }]) => !existing.some((e) => e.date === date && e.type === type))
            .map(
                ([date, { type, isHalfDay }]) =>
                    new TimeEntry({
                        type,
                        employeeId: this._absence.employeeId,
                        date,
                        days: (isHalfDay ? 0.5 : 1) as Days,
                    })
            );

        // Update existing entries with correct days value
        existing.forEach((e) => {
            const day = this._absenceDays.get(e.date);
            if (day) {
                e.days = (day.isHalfDay ? 0.5 : 1) as Days;
            }
        });

        return [...existing, ...added];
    }

    private get _conflictingEntries() {
        const data = this._data;
        if (!data || !data.end) {
            return [];
        }
        const allowedTypes = this._includedTypes;
        return this._entries.filter(
            (e) => e.date >= data.start && e.date < data.end! && !allowedTypes.includes(e.type)
        );
    }

    private get _dialogTitle() {
        const { id, status, type } = this._absence;
        switch (status) {
            case AbsenceStatus.Requested:
                return type === TimeEntryType.Vacation ? "Urlaubsantrag Beantworten" : "Krankmeldung Beantworten";
            default:
                if (status === AbsenceStatus.Inferred || Boolean(id)) {
                    return type === TimeEntryType.Vacation ? "Urlaubseintrag Bearbeiten" : "Krankmeldung Bearbeiten";
                } else {
                    return type === TimeEntryType.Vacation ? "Urlaubseintrag Erstellen" : "Krankmeldung Erstellen";
                }
        }
    }

    async show(absence: Absence) {
        const promise = super.show();
        this._lastRangeLoaded = undefined;
        this._absenceDays.clear();
        this._absence = absence;
        await this.updateComplete;
        this._fill();

        await this._load();

        setTimeout(() => {
            if (!this._startInput.value) {
                void this._startInput.focus();
            } else if (!this._endInput.value) {
                void this._endInput.focus();
            } else {
                void this._notesInput.focus();
            }
        }, 500);

        return promise;
    }

    private _lastRangeLoaded?: DateRange;

    private get _maxMonth() {
        return this._absence.type === TimeEntryType.Vacation
            ? dateAdd(toDateString(new Date()), {
                  months: GLOBAL_CONSTANTS.VACATION_FUTURE_LIMIT_MONTHS,
              })
            : undefined;
    }

    private async _load() {
        this._validationError = "";

        if (!this._data || !this._data.start || !this._data.end) {
            return;
        }

        const { start, end } = this._data;

        if (end <= start) {
            this._validationError = "Das Abwesenheitsende darf nicht vor dem Abwesenheitsbeginn liegen!";
            return;
        }

        if (start > dateAdd(toDateString(new Date()), { months: GLOBAL_CONSTANTS.VACATION_FUTURE_LIMIT_MONTHS })) {
            this._validationError = "Der Abwesenheitsbeginn darf nicht länger als 18 Monate in der Zukunft liegen.";
            return;
        }

        if (start < dateAdd(toDateString(new Date()), { days: -365 })) {
            this._validationError =
                "Der Abwesenheitsbeginn darf nicht weiter als ein Jahr in der Vergangenheit liegen.";
            return;
        }

        if (dateSub(start, end) > 180) {
            this._validationError = "Die Abwesenheitsdauer darf nicht länger als 180 Tage betragen!";
            return;
        }

        const from = getRange(start, "month").from;
        const to = getRange(end, "month").to;

        if (this._lastRangeLoaded && from >= this._lastRangeLoaded.from && to <= this._lastRangeLoaded.to) {
            this.requestUpdate();
            await this.updateComplete;
            this._fillDays();
            this.requestUpdate();
            return;
        }

        this.loading = true;
        try {
            const [vacationBalances, entries, commonWorkDays, rosterNotes] = await Promise.all([
                app.api.getVacationBalances(
                    new GetBalancesParams({
                        filters: [{ type: "employeeId", value: this._absence.employeeId }],
                        resolution: "year",
                        from: getRange(start, "year").from,
                        to: getRange(end, "year").to,
                    })
                ),
                app.getTimeEntries({
                    employee: this._absence.employeeId,
                    from,
                    to,
                    type: [
                        TimeEntryType.Work,
                        TimeEntryType.Vacation,
                        TimeEntryType.Sick,
                        TimeEntryType.SickInKUG,
                        TimeEntryType.ChildSick,
                        TimeEntryType.Free,
                        TimeEntryType.CompDay,
                    ],
                }),
                app.api.getCommonWorkDays(
                    new GetCommonWorkDaysParams({
                        employeeId: this._absence.employeeId,
                        before: start,
                    })
                ),
                app.api.getRosterNotes(
                    new GetRosterNotesParams({
                        from,
                        to,
                        filters: [{ type: "employeeId", value: this._absence.employeeId }],
                    })
                ),
            ]);
            this._vacationBalances = vacationBalances;
            this._entries = entries;
            this._commonWorkDays = commonWorkDays;
            this._rosterNotes = rosterNotes;
            this._lastRangeLoaded = { from, to };
        } catch (e) {
            void alert(`Fehler beim Laden des Abwesenheitendialogs; ${e.message}`, { type: "warning" });
        }
        this.loading = false;

        this._fillDays();
        this.requestUpdate();
    }

    private _fill() {
        this._startInput.value = this._absence.start;
        this._endInput.value = this._absence.end ? dateAdd(this._absence.end, { days: -1 }) : null;
        this._notesInput.value = this._absence.notes;
    }

    /**
     * Populates the absence days map based on the absence state and existing entries.
     */
    private _fillDays() {
        this._absenceDays.clear();

        if (!this._data?.start || !this._data?.end) {
            return;
        }

        // Determine the current state of the absence
        const isApproved = this._absence.id && this._absence.status === AbsenceStatus.Approved;
        const isNewOrRequested = !this._absence.id || this._absence.status === AbsenceStatus.Requested;
        const hasExistingEntries = this._entries.some(
            (e) => e.type === this._absence.type && e.date >= this._absence.start && e.date < this._absence.end
        );
        const shouldPrefillFromWorkDays = isNewOrRequested && !hasExistingEntries;

        // Iterate through the date range and apply the appropriate fill strategy
        for (let date = this._data.start; date < this._data.end; date = dateAdd(date, { days: 1 })) {
            if (isApproved) {
                // Preserve approved days exactly as they were approved.

                this._fillApprovedDay(date);
            } else if (shouldPrefillFromWorkDays) {
                // Smart prefill based on the employee's work schedule.
                this._fillCommonWorkDay(date);
            } else {
                // Use existing entries from previous edits or partial submissions.
                this._fillExistingEntry(date);
            }
        }

        this.requestUpdate();
    }

    private _fillApprovedDay(date: DateString): void {
        const includedTypes = this._includedTypes;
        const primaryEntry = this._entries.find((e) => e.date === date && e.type === this._absence.type);
        const fallbackEntry = this._entries.find((e) => e.date === date && includedTypes.includes(e.type));
        const entry = primaryEntry || fallbackEntry;
        if (entry) {
            this._absenceDays.set(date, {
                type: entry.type,
                isHalfDay: isHalfDayEntry(entry),
            });
        }
    }

    private _fillCommonWorkDay(date: DateString): void {
        if (this._isCommonDay(date)) {
            this._absenceDays.set(date, {
                type: this._absence.type,
                isHalfDay: false,
            });
        }
    }

    private _fillExistingEntry(date: DateString): void {
        const entry = this._entries.find((e) => e.date === date);
        if (entry) {
            this._absenceDays.set(date, {
                type: entry.type,
                isHalfDay: isHalfDayEntry(entry),
            });
        }
    }

    private _isCommonDay(date: string) {
        const d = parseDateString(date)!;
        const contract = this._employee.getContractForDate(date);
        if (contract && contract.fixedWorkDays) {
            return !!contract.hoursPerDay && contract.hoursPerDay[(d.getDay() + 6) % 7] !== 0;
        }
        return this._commonWorkDays.includes(d.getDay());
    }

    private async _submit(e?: FocusEvent, vals: Partial<Absence> = {}) {
        e && e.preventDefault();
        const { start, end, notes } = this._data!;

        const result: Result = {};

        // Calculate total days from selected absence days
        const dayCount = [...this._absenceDays.values()].reduce((sum, { isHalfDay }) => sum + (isHalfDay ? 0.5 : 1), 0);

        const absence = new Absence({
            ...this._absence,
            ...vals,
            start,
            end: end!,
            notes,
            dayCount,
        });

        if (!absence.status) {
            absence.status = AbsenceStatus.Approved;
            absence.response = {
                employeeId: app.profile!.id,
                time: new Date(),
                message: "",
            };
        }

        if (absence.status === AbsenceStatus.Inferred) {
            absence.status = AbsenceStatus.Approved;
        }

        const updateEntries = this._updatedEntries;
        const deleteEntries = this._removedEntries;
        const conflictingEntries = this._conflictingEntries;

        updateEntries.forEach((e) => (e.comment = notes));

        let cleanupConflictingShifts: Promise<unknown> = Promise.resolve();

        if (absence.status === AbsenceStatus.Approved) {
            if (deleteEntries.length) {
                this.hide();
                const confirmed = await confirm(
                    `Wollen Sie diese Änderung wirklich vornehmen? Es werden ${deleteEntries.length} Fehltage gelöscht.`,
                    "Bestätigen",
                    "Abbrechen",
                    {
                        title: "Änderung Speichern",
                    }
                );
                this._show();
                if (!confirmed) {
                    return;
                }
            }

            if (
                conflictingEntries.some(
                    (entry) => entry.isPast && !entry.deleted && (entry.startFinal || entry.endFinal)
                )
            ) {
                this.hide();

                const choice = await alert(
                    html`Es liegen bereits erfasste Arbeitszeiten in diesem Zeitraum vor! Möchten Sie diese Einträge
                    löschen oder als nicht zugewiesen markieren?`,
                    {
                        options: [
                            { label: "Als Nicht Zugewiesen Markieren", buttonClass: "primary" },
                            { label: "Einträge Behalten", buttonClass: "secondary" },
                            { label: "Einträge Löschen", buttonClass: "negative" },
                            { label: "Abbrechen" },
                        ],
                        title: "Konflikte mit Arbeitszeiten",
                        type: "destructive",
                        icon: "icon.status.warning",
                        optionsLayout: "vertical",
                    }
                );

                this._show();
                switch (choice) {
                    // First option selected: Mark as unassigned
                    case 0:
                        conflictingEntries.forEach((e) => {
                            assignEmployee(e, null);
                            e.published = null;
                            e.seen = null;
                        });
                        cleanupConflictingShifts = app.createOrUpdateTimeEntries(conflictingEntries, {
                            wait: true,
                            publish: false,
                        });
                        await app.updateSettings({ rosterDisplayUnassigned: true });
                        result.shiftsUnassigned = true;
                        break;
                    // Second option selected: Keep
                    case 1:
                        break;
                    // Third option selected: Delete
                    case 2:
                        cleanupConflictingShifts = app.removeTimeEntries(conflictingEntries, true, true);
                        break;
                    // Fourth/Fallback option selected: Cancel
                    default:
                        return;
                }
            } else if (conflictingEntries.length) {
                this.hide();
                const choice = await alert(
                    html`Im dem gewählten Zeitraum liegen
                        <strong>${conflictingEntries.length} bestehende Dienstplaneinträge</strong> für diesen
                        Mitarbeiter vor. Möchten Sie diese Einträge löschen oder als nicht zugewiesen markieren?`,
                    {
                        options: [
                            { label: "Als Nicht Zugewiesen Markieren", buttonClass: "primary" },
                            { label: "Einträge Behalten", buttonClass: "secondary" },
                            { label: "Einträge Löschen", buttonClass: "negative" },
                            { label: "Abbrechen" },
                        ],
                        title: "Konflikte mit geplanten Schichten",
                        type: "destructive",
                        icon: "icon.status.warning",
                        optionsLayout: "vertical",
                    }
                );
                this._show();
                switch (choice) {
                    // First option selected: Mark as unassigned
                    case 0:
                        conflictingEntries.forEach((e) => {
                            assignEmployee(e, null);
                            e.published = null;
                            e.seen = null;
                        });
                        cleanupConflictingShifts = app.createOrUpdateTimeEntries(conflictingEntries, {
                            wait: true,
                            publish: false,
                        });
                        await app.updateSettings({ rosterDisplayUnassigned: true });
                        result.shiftsUnassigned = true;
                        break;
                    // Second option selected: Keep
                    case 1:
                        break;
                    // Third option selected: Delete
                    case 2:
                        cleanupConflictingShifts = app.removeTimeEntries(conflictingEntries, true, true);
                        break;
                    // Fourth/Fallback option selected: Cancel
                    default:
                        return;
                }
            }
        }

        this.loading = true;

        try {
            await cleanupConflictingShifts;

            if (absence.id) {
                result.updated = true;
                await app.api.updateAbsence(absence);
            } else {
                result.created = true;
                await app.api.createAbsence(absence);
            }

            if (absence.status === AbsenceStatus.Approved) {
                if (deleteEntries.length) {
                    await app.removeTimeEntries(deleteEntries, true, true);
                }
                if (updateEntries.length) {
                    await app.createOrUpdateTimeEntries(updateEntries, { wait: true, publish: true });
                }
            }

            this.done(result);
        } catch (e) {
            void alert(`Fehler beim Abschicken der Änderungen: ${e.message}`, { type: "warning" });
        }

        this.loading = false;
    }

    private async _approve() {
        const v = this._absence.type === TimeEntryType.Vacation;
        const message = await prompt(
            `Wollen Sie ${v ? "diesen Urlaubsantrag" : "diese Krankmeldung"} wirklich genehmigen?`,
            "Absenden",
            "Abbrechen",
            {
                title: `${v ? "Urlaubsantrag" : "Krankmeldung"} Genehmigen`,
                icon: "icon.status.approved",
                placeholder: "Geben Sie hier Ihre optionale Nachricht an den Mitarbeiter ein.",
            }
        );

        if (typeof message !== "undefined") {
            return this._submit(undefined, {
                status: AbsenceStatus.Approved,
                response: {
                    employeeId: app.profile!.id,
                    time: new Date(),
                    message,
                },
            });
        }
    }

    private async _deny() {
        this.hide();
        const v = this._absence.type === TimeEntryType.Vacation;
        const message = await prompt(
            `Wollen Sie ${v ? "diesen Urlaubsantrag" : "diese Krankmeldung"} wirklich ablehnen?`,
            "Absenden",
            "Abbrechen",
            {
                title: `${v ? "Urlaubsantrag" : "Krankmeldung"} Ablehnen`,
                icon: "icon.status.rejected",
                placeholder: "Geben Sie hier Ihre optionale Nachricht an den Mitarbeiter ein.",
            }
        );
        this._show();
        if (typeof message !== "undefined") {
            return this._submit(undefined, {
                status: AbsenceStatus.Denied,
                response: {
                    employeeId: app.profile!.id,
                    time: new Date(),
                    message,
                },
            });
        }
    }

    private async _deleteAbsence() {
        if (!this._absence) {
            return;
        }
        const includedTypes = this._includedTypes;
        const deleteEntries = this._entries.filter(
            (e) => includedTypes.includes(e.type) && e.date >= this._absence.start && e.date < this._absence.end
        );
        this.hide();
        const confirmed = await confirm(
            `Wollen Sie diese Abwesenheit wirklich entfernen? Es werden ${deleteEntries.length} Fehltage gelöscht.`,
            "Löschen",
            "Abbrechen",
            {
                title: "Abwesenheit Löschen",
                type: "destructive",
            }
        );
        if (confirmed) {
            try {
                await Promise.all([
                    deleteEntries.length ? app.removeTimeEntries(deleteEntries, true, true) : Promise.resolve(),
                    this._absence.id ? app.api.deleteAbsence(this._absence.id) : Promise.resolve(),
                ]);
                this.done({ deleted: true });
            } catch (e) {
                await alert(e.message, { type: "warning" });
                this._show();
            }
        } else {
            this._show();
        }
    }

    private _getCycleSteps(date: DateString, type: AbsenceType) {
        const baseCycle = (entryType: AbsenceType) => [
            { type: entryType, isHalfDay: false },
            { type: "none", isHalfDay: false },
        ];

        switch (type) {
            case TimeEntryType.Sick:
            case TimeEntryType.ChildSick:
            case TimeEntryType.SickInKUG:
            case TimeEntryType.CompDay:
                return baseCycle(type);
            case TimeEntryType.Vacation: {
                const holiday = getHolidayForDate(date, { country: app.company?.country });
                const halfDayEnabled = this._enabledFeatures?.halfDayVacations ?? false;

                return [
                    { type: TimeEntryType.Vacation, isHalfDay: false },
                    ...(halfDayEnabled ? [{ type: TimeEntryType.Vacation, isHalfDay: true }] : []),
                    ...(holiday ? [{ type: TimeEntryType.CompDay, isHalfDay: false }] : []),
                    { type: "none", isHalfDay: false },
                ];
            }
        }
    }

    private _toggleDay(date: DateString, type: AbsenceType, ev: Event) {
        const selected = this._absenceDays.get(date);
        const cycle = this._getCycleSteps(date, type);

        const currentIndex = cycle.findIndex(
            (state) => state.type === (selected?.type ?? "none") && state.isHalfDay === (selected?.isHalfDay ?? false)
        );

        const nextState = cycle[(currentIndex + 1) % cycle.length];

        if (nextState.type === "none") {
            this._absenceDays.delete(date);
        } else {
            this._absenceDays.set(date, {
                type: nextState.type as TimeEntryType,
                isHalfDay: nextState.isHalfDay,
            });
        }

        (ev.currentTarget as HTMLElement).animate(
            [{ transform: "scale(1)" }, { transform: "scale(0.9)" }, { transform: "scale(1)" }],
            { duration: 300, easing: "cubic-bezier(0.34, 1.56, 0.64, 1)" }
        );

        this.requestUpdate();
    }

    static styles = [
        ...Dialog.styles,
        DateInput.styles,
        Textarea.styles,
        css`
            .inner {
                max-width: 30em;
            }

            .day-grid {
                display: grid;
                grid-template-columns: repeat(7, 3.5em);
                justify-content: center;
                grid-gap: 0.5em;
            }

            .day-header {
                font-weight: bold;
                text-align: center;
            }

            .day {
                box-sizing: border-box;
                height: 3.5em;
                border-radius: 0.5em;
                border: solid 1px var(--shade-2);
                overflow: hidden;
                transition-duration: 0.3s, 0.1s, 0.1s;
                transition-property: filter, background-color, color;
            }

            .day:active:not([disabled]) {
                filter: blur(2px);
            }

            .day:not(disabled) {
                cursor: pointer;
            }

            .day.blocked {
                border-color: var(--red);
            }

            .day.blocked {
                border-color: var(--red);
            }

            .day-date {
                position: absolute;
                right: 0.4em;
                top: 0.4em;
                line-height: 1em;
                font-weight: bold;
                z-index: 1;
                pointer-events: none;
                user-select: none;
            }

            .day {
                &::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    border-radius: inherit;
                    z-index: -1;
                    background: var(--color-highlight);
                    clip-path: var(--path-diagonal-half-empty);
                    transition: clip-path 0.2s ease-in-out;
                }
            }

            .day.selected {
                color: var(--color-bg);

                &::before {
                    clip-path: var(--path-diagonal-half-full);
                }
            }

            .day[data-type="halfVacation"] {
                background: color-mix(in oklch, var(--color-highlight), transparent);

                &::before {
                    clip-path: var(--path-diagonal-half);
                }
            }

            .day .selected-icon {
                position: absolute;
                left: 0.2em;
                top: 0.2em;
            }

            .day-is-common,
            .conflict,
            .vacation-block,
            .holiday {
                color: var(--color-highlight);
                font-size: var(--font-size-smaller);
            }

            .day .day-is-common {
                position: absolute;
                left: 0.2em;
                bottom: 0.2em;
            }

            .day .conflict,
            .day .vacation-block,
            .day .holiday {
                position: absolute;
                right: 0.2em;
                bottom: 0.2em;
            }

            ptc-ui-progress {
                width: 8em;
            }
            ptc-ui-progress.vacation {
                --progress-color: var(--blue);
                --progress-color-below: var(--blue);
            }

            .legend span {
                white-space: nowrap;
            }
        `,
    ];

    private _renderDay(date: DateString, type: AbsenceType) {
        const data = this._data;
        if (!data) {
            return;
        }
        const conflictingEntries = this._conflictingEntries;
        const commitBefore = app.company?.settings.commitTimeEntriesBefore;
        const conflicting = conflictingEntries.some((e) => e.date === date);
        const commited = commitBefore && date < commitBefore;
        const blocked =
            this._absence.type === TimeEntryType.Vacation &&
            this._rosterNotes.some(
                (n) => n.start <= date && n.end > date && n.vacationPolicy === VacationPolicy.DisallowAll
            );
        const holiday = getHolidayForDate(date, { country: app.company?.country });
        const { type: selectedDayType, isHalfDay } = this._absenceDays.get(date) || {};

        // Determine data-type for CSS styling
        const dataType = () => {
            if (selectedDayType === TimeEntryType.Vacation) {
                return isHalfDay ? "halfVacation" : "vacation";
            } else if (selectedDayType === TimeEntryType.CompDay) {
                return "compDay";
            }

            return "";
        };

        const classes = classMap({
            day: true,
            relative: true,
            blocked,
            selected: !!selectedDayType,
        });

        return html`<div
            class="${classes}"
            data-type=${dataType()}
            data-category=${type}
            ?disabled=${!data.start || !data.end || date < data.start || date >= data.end || commited || blocked}
            @click=${(ev: Event) => this._toggleDay(date, type, ev)}
            style=${styleMap({
                "--color-highlight": selectedDayType && timeEntryTypeColor(selectedDayType),
            })}
        >
            <div class="day-date">${parseDateString(date)!.getDate()}</div>

            ${selectedDayType
                ? html`
                      <ptc-ui-icon
                          name="${app.localized.timeEntryTypeIcon(selectedDayType)}"
                          class="selected-icon"
                      ></ptc-ui-icon>
                  `
                : ""}

            <ptc-ui-icon
                name="icon.object.common-workday"
                class="day-is-common green"
                ?hidden=${!this._isCommonDay(date)}
            ></ptc-ui-icon>

            ${blocked
                ? html` <ptc-ui-icon name="icon.status.blocked" class="orange vacation-block"></ptc-ui-icon> `
                : conflicting
                  ? html` <ptc-ui-icon name="icon.status.warning" class="red conflict"></ptc-ui-icon> `
                  : holiday
                    ? html`<ptc-ui-icon
                          name="icon.event.holiday"
                          class="purple holiday"
                          title=${holiday.name}
                      ></ptc-ui-icon>`
                    : ""}
        </div> `;
    }

    renderContent() {
        const absence = this._absence;
        const employee = this._employee;
        const data = this._data;
        const days = this._days;
        const type = this._absence.type as AbsenceType; // @TODO: remove typecast after fixing the type in the model
        const editing = this._absence.id || this._absence.status === AbsenceStatus.Inferred;
        const rosterNotes =
            (data?.start &&
                data?.end &&
                this._rosterNotes.filter((n) => n.start < data!.end! && n.end > data!.start!)) ||
            [];
        const blocked =
            this._absence.type === TimeEntryType.Vacation &&
            rosterNotes.some((n) => n.vacationPolicy === VacationPolicy.DisallowAll);
        const maxMonth = this._maxMonth;

        const datePickerBadge = (date: string) => {
            const blocked =
                this._absence.type === TimeEntryType.Vacation &&
                this._rosterNotes.some(
                    (n) => n.start <= date && n.end > date && n.vacationPolicy === VacationPolicy.DisallowAll
                );
            return blocked
                ? {
                      icon: "icon.status.blocked",
                      class: "red",
                  }
                : undefined;
        };

        return html`
            <form @submit=${this._submit} class="vertical layout fit-vertically" @input=${() => this.requestUpdate()}>
                <div class="padded horizontal center-aligning layout border-bottom">
                    <div class="padded stretch semibold">
                        <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(type)}"></ptc-ui-icon>
                        ${this._dialogTitle}
                    </div>
                    <button
                        type="button"
                        class="slim red transparent"
                        @click=${this._deleteAbsence}
                        ?hidden=${!editing}
                    >
                        <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                    </button>
                    <button type="button" class="slim transparent" @click=${() => this.done()}>
                        <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                    </button>
                </div>
                <div class="padded">
                    <label>Mitarbeiter</label>
                    <div class="larger horizontally-padded bottom-margined spacing center-aligning horizontal layout">
                        <ptc-avatar class="tinier" .employee=${employee}></ptc-avatar>
                        <div>${employee.name}</div>
                    </div>

                    <div class="horizontal evenly stretching spacing layout">
                        <div class="vertical layout">
                            <label>Von</label>
                            <ptc-date-input
                                name="start"
                                required
                                @input=${async () => {
                                    await this._load();
                                }}
                                .readonly=${[AbsenceStatus.Requested, AbsenceStatus.Denied].includes(absence.status)}
                                .max=${maxMonth}
                                datePicker="popover"
                                .datePickerBadge=${datePickerBadge}
                            ></ptc-date-input>
                        </div>
                        <div class="vertical layout">
                            <label>Bis</label>
                            <ptc-date-input
                                name="end"
                                required
                                @input=${async () => {
                                    await this._load();
                                }}
                                .min=${data && data.start}
                                .max=${maxMonth}
                                .readonly=${[AbsenceStatus.Requested, AbsenceStatus.Denied].includes(absence.status)}
                                datePicker="popover"
                                .datePickerBadge=${datePickerBadge}
                            ></ptc-date-input>
                        </div>
                    </div>
                </div>

                <ptc-scroller class="stretch">
                    <div class="horizontally-padded">
                        ${absence.request
                            ? html`
                                  <div class="smaller padded bottom-margined text-centering red box">
                                      <strong>
                                          <ptc-ui-icon name="icon.planning.vacation-request"></ptc-ui-icon>
                                          ${absence.type.includes("sick") ? "Eingereicht" : "Beantragt"} am
                                          ${formatDate(absence.request.time)}:
                                      </strong>
                                      ${absence.request.message
                                          ? html` <span>"${absence.request.message}"</span> `
                                          : ""}
                                  </div>
                              `
                            : ""}
                        ${absence.response
                            ? html`
                                  ${absence.status === AbsenceStatus.Denied
                                      ? html`
                                            <div class="smaller padded bottom-margined text-centering red box">
                                                <strong>
                                                    <ptc-ui-icon name="icon.status.rejected"></ptc-ui-icon> Abgelehnt
                                                    von ${absence.response.employeeName || "Unbekannt"} am
                                                    ${formatDate(absence.response.time)}:
                                                </strong>
                                                ${absence.response.message
                                                    ? html` <span>"${absence.response.message}"</span> `
                                                    : ""}
                                            </div>
                                        `
                                      : html`
                                            <div class="smaller padded bottom-margined text-centering green box">
                                                <strong>
                                                    <ptc-ui-icon name="icon.status.approved"></ptc-ui-icon>
                                                    ${absence.type.includes("sick") ? "Erfasst" : "Genehmigt"} von
                                                    ${absence.response.employeeName || "Unbekannt"} am
                                                    ${formatDate(absence.response.time)}:
                                                </strong>
                                                ${absence.response.message
                                                    ? html` <span>"${absence.response.message}"</span> `
                                                    : ""}
                                            </div>
                                        `}
                              `
                            : ""}
                        ${this._validationError
                            ? html`<div class="double-margined double-padded red colored-text text-centering">
                                  <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon> ${this._validationError}
                              </div>`
                            : ""}
                        ${!data || !data.start || !data.end
                            ? html`<div class="double-margined double-padded text-centering">
                                  <ptc-ui-icon name="icon.object.date"></ptc-ui-icon> Bitte geben Sie den
                                  Abwesenheitszeitraum ein!
                              </div>`
                            : ""}
                        ${blocked
                            ? html`
                                  <div class="small text-centering padded red box">
                                      <ptc-ui-icon name="icon.status.blocked"></ptc-ui-icon> Urlaubssperre für den
                                      gewählten Zeitraum!
                                  </div>
                              `
                            : ""}
                        ${rosterNotes.length
                            ? html`
                                  <div class="double-margined">
                                      <div class="subtle margined text-centering">
                                          <ptc-ui-icon name="icon.object.date"></ptc-ui-icon> Kalendereinträge
                                      </div>
                                      <div class="spacing vertical layout">
                                          ${rosterNotes.map(
                                              (rosterNote) => html`
                                                  <ptc-roster-note
                                                      class="small"
                                                      .rosterNote=${rosterNote}
                                                      .employee=${employee}
                                                  ></ptc-roster-note>
                                              `
                                          )}
                                      </div>
                                  </div>
                              `
                            : ""}
                        ${absence.status !== AbsenceStatus.Denied && !this._validationError && data && days
                            ? html`
                                  <div class="subtle double-margined text-centering">
                                      Bitten wählen Sie die zu buchenden Fehltage:
                                  </div>

                                  <div class="day-grid">
                                      <div class="day-header">Mo</div>
                                      <div class="day-header">Di</div>
                                      <div class="day-header">Mi</div>
                                      <div class="day-header">Do</div>
                                      <div class="day-header">Fr</div>
                                      <div class="day-header">Sa</div>
                                      <div class="day-header">So</div>
                                  </div>

                                  <div
                                      class="vertically-margined day-grid"
                                      style="--color-highlight: ${timeEntryTypeColor(type)}"
                                  >
                                      ${days.map((date) => this._renderDay(date, type))}
                                  </div>

                                  <div class="top-margined stats centering spacing vertical layout text-centering">
                                      ${absence.type === TimeEntryType.Vacation
                                          ? this._renderVacationBalances(this._vacationBalances)
                                          : nothing}
                                  </div>

                                  <div class="legend double-margined text-centering small subtle">
                                      <span>
                                          <ptc-ui-icon
                                              name="icon.object.common-workday"
                                              class="green day-is-common"
                                          ></ptc-ui-icon>
                                          Übliche Arbeitstage
                                          ${this._commonWorkDays.length > 0 ? "" : "(erst nach 13 Wochen)"}
                                      </span>
                                      <span>
                                          <ptc-ui-icon
                                              name="icon.status.blocked"
                                              class="red vacation-block"
                                          ></ptc-ui-icon>
                                          Urlaubssperre
                                      </span>
                                      <span>
                                          <ptc-ui-icon name="icon.event.holiday" class="purple holiday"></ptc-ui-icon>
                                          Feiertag
                                      </span>
                                      <span>
                                          <ptc-ui-icon name="icon.status.warning" class="orange conflict"></ptc-ui-icon>
                                          Konflikte mit bestehenden Dienstplaneinträgen
                                      </span>
                                  </div>
                              `
                            : ""}

                        <div class="field">
                            <label>Bemerkungen</label>
                            <ptc-textarea name="notes"></ptc-textarea>
                        </div>
                    </div>
                </ptc-scroller>

                <div class="padded">
                    ${absence.status === AbsenceStatus.Denied
                        ? html`
                              <div class="spacing evenly stretching horizontal layout">
                                  <button class="transparent" type="button" @click=${() => this.dismiss()}>
                                      Schließen
                                  </button>
                              </div>
                          `
                        : absence.status === AbsenceStatus.Requested
                          ? html`
                                <div class="spacing evenly stretching horizontal layout">
                                    <button
                                        class="green ghost"
                                        type="button"
                                        @click=${this._approve}
                                        ?disabled=${!!this._validationError}
                                    >
                                        <ptc-ui-icon name="icon.status.approved"></ptc-ui-icon> Genehmigen
                                    </button>
                                    <button class="red ghost" type="button" @click=${this._deny}>
                                        <ptc-ui-icon name="icon.status.rejected"></ptc-ui-icon> Ablehnen
                                    </button>
                                </div>
                            `
                          : html`
                                <div class="spacing evenly stretching horizontal layout">
                                    <button class="primary" ?disabled=${blocked || !!this._validationError}>
                                        Speichern
                                    </button>
                                    <button class="transparent" type="button" @click=${() => this.done()}>
                                        Abbrechen
                                    </button>
                                </div>
                            `}
                </div>
            </form>
        `;
    }

    private _renderVacationBalances(balances: VacationBalance[]) {
        const vacationAbsenceDays = [...this._absenceDays.entries()].filter(
            ([, { type }]) => type === TimeEntryType.Vacation
        );

        return balances
            .flatMap((balance) => balance.subBalances ?? [balance])
            .map((balance) => {
                const { taken, available } = getVacationEntitlement(balance);

                const before = this._includedEntries
                    .filter((e) => e.date >= balance.from && e.date < balance.to)
                    .reduce((sum, e) => sum + (e.days ?? 1), 0);

                const after = vacationAbsenceDays
                    .filter(([date]) => date >= balance.from && date < balance.to)
                    .reduce((sum, [, { isHalfDay }]) => sum + (isHalfDay ? 0.5 : 1), 0);

                const diff = after - before;

                return html`
                    <div
                        class="center-aligning wrapping spacing horizontal layout"
                        style="--progress-color-subceeded: black; --progress-color-below: black;"
                    >
                        <div class="semibold blue colored-text stats-label">Urlaub ${formatRange(balance)}:</div>
                        <ptc-ui-progress .max=${available} .value=${taken} class="vacation"></ptc-ui-progress>
                        ${diff
                            ? html` <ptc-ui-icon name="icon.navigation.forward" class="smaller"></ptc-ui-icon>
                                  <ptc-ui-progress
                                      .max=${available}
                                      .value=${taken + diff}
                                      class="vacation"
                                  ></ptc-ui-progress>`
                            : ""}
                    </div>
                `;
            });
    }
}
